import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import Layout from "../layout/Layout";
import { apiV2 } from "../../../api/axiosConfig";
import BasicText from "../../../components/form-v2/basic-text";
import { BlueButton } from "../../../components/ui/Buttons";
import { ClientSelectSingle } from "../../../components/form-v2/searchable-select/ClientSelect";
import { DepartmentSelectV2 } from "../../../lib/departments/DepartmentSelectV2";
import { Client, Department } from "../../../types/userManagement";
import { useAuth } from "../../../context/user";
import { useUserManagementBrowsing } from "../../../lib/user-management/UserManagementBrowsingContext";
import { UserSelector } from "../../../components/paginated-selector/user-selector";
import ModalV2 from "../../../components/portal/ModalV2";

const NotificationGroupCreate: React.FC = () => {
    const navigate = useNavigate();
    const auth = useAuth();

    const [name, setName] = useState("");
    const [userIDs, setUserIDs] = useState<string[]>([]);
    const [client, setClient] = useState<Client | undefined>(undefined);
    const [department, setDepartment] = useState<Department | undefined>(undefined);
    const handleClientChange = (c: Client | undefined) => {
        setClient(c);
        setDepartment(undefined);
        setUserIDs([]);
    };
    const handleDepartmentChange = (d: Department | undefined) => {
        setDepartment(d);
        setUserIDs([]);
    };
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const clientID = auth.isSuperAdmin ? client?.id : auth.clientID;
    const departmentID = department?.id;
    const canSave = !!name && (!auth.isDepartmentAdmin || !!departmentID);

    const save = async () => {
        setSaving(true);
        try {
            await apiV2.post("notification-groups", { clientID, departmentID, name, userIDs });
            navigate("/management/notification-groups?message=Notification group created");
        } catch (e) {
            if (isAxiosError(e) && e?.response?.data?.error) {
                setError(e.response.data.error);
            } else {
                setError("An error has occurred");
            }
            setSaving(false);
        }
    };

    return (
        <Layout active="notification-groups">
            <div className="flex flex-col gap-8">
                <div className="bg-white shadow-md p-4 w-1/2 flex flex-col gap-8">
                    <p className="text-md font-semibold">Create Notification Group</p>
                    <BasicText value={name} onChange={setName} label="Name" />
                    {auth.isSuperAdmin && (
                        <div className="flex flex-col">
                            <label className="mb-1">Client</label>
                            <ClientSelectSingle client={client} onChange={handleClientChange} />
                        </div>
                    )}
                    {(auth.isSuperAdmin || auth.isClientAdmin || auth.isDepartmentAdmin) && (
                        <div className="flex flex-col">
                            <label className="mb-1">Department</label>
                            <DepartmentSelectV2 key={clientID ?? ""} clientID={clientID} onChange={handleDepartmentChange} isDisabled={auth.isSuperAdmin && !clientID} />
                        </div>
                    )}
                    <div className="flex flex-col">
                        <label className="mb-1">Users</label>
                        <button
                            type="button"
                            onClick={() => setShowUsersModal(true)}
                            disabled={auth.isDepartmentAdmin && !departmentID}
                            className="h-10 w-full shadow-md rounded-md px-4 bg-white hover:bg-gray-50 flex items-center justify-between text-left disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className={userIDs.length === 0 ? "text-gray-400" : ""}>
                                {userIDs.length === 0
                                    ? "Select users"
                                    : `${userIDs.length} user${userIDs.length === 1 ? "" : "s"} selected`}
                            </span>
                            <span className="text-gray-400">▼</span>
                        </button>
                    </div>
                    {clientID && (
                        <ModalV2 open={showUsersModal} setOpen={setShowUsersModal} className="pt-10">
                            <UserSelector ids={userIDs} onChange={setUserIDs} clientID={clientID} departmentID={departmentID} />
                        </ModalV2>
                    )}
                    <BlueButton text="Create Group" disabled={!canSave} onClick={save} loading={saving} />
                    {error && <p className="text-center text-red">{error}</p>}
                </div>
            </div>
        </Layout>
    );
};

NotificationGroupCreate.displayName = "NotificationGroupCreate";

export default NotificationGroupCreate;
